import { Component, computed, inject, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { RouterModule, Router } from '@angular/router';
import { MatCardModule } from '@angular/material/card';
import { CurrentUserState } from '../../state/current-user.state';
import { ChartConfiguration, ChartData, ChartEvent, ChartType, Chart } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';

interface QuickAccessCard {
  title: string;
  description: string;
  icon: string;
  route: string;
  color: string;
  section: string;
  subsection: string;
  disabled?: boolean;
}

@Component({
  selector: 'app-main',
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule,
    RouterModule,
    MatCardModule
  ],
  templateUrl: './main.component.html',
  styleUrls: ['./main.component.scss']
})
export class MainComponent {
  private currentUserState = inject(CurrentUserState);
  private router = inject(Router);

  constructor() {
    Chart.register(ChartDataLabels);
  }

  private allCards: QuickAccessCard[] = [
    {
      title: 'Administration',
      description: 'User and customer management',
      icon: 'settings',
      route: '/users',
      color: '#455a64',
      section: 'Administration',
      subsection: 'Users'
    },
    {
      title: 'Configuration',
      description: 'System configuration and settings',
      icon: 'build',
      route: '/configuration/currencies',
      color: '#546e7a',
      section: 'Configuration',
      subsection: 'Currencies'
    },
    {
      title: 'Companies',
      description: 'View and manage company records',
      icon: 'business',
      route: '/companies/list',
      color: '#1976d2',
      section: 'Companies',
      subsection: 'Companies List'
    },
    {
      title: 'Directors',
      description: 'Browse director profiles and information',
      icon: 'groups',
      route: '/directors/list',
      color: '#7b1fa2',
      section: 'Directors',
      subsection: 'Directors List'
    },
    {
      title: 'Large Shareholders',
      description: 'Track major shareholders and ownership',
      icon: 'insights',
      route: '/large-shareholders/list',
      color: '#388e3c',
      section: 'Large Shareholders',
      subsection: 'Large Shareholders List'
    },
    {
      title: 'Audit Information',
      description: 'Access audit reports and standards',
      icon: 'fact_check',
      route: '/audit/audit-firms',
      color: '#d32f2f',
      section: 'Audit',
      subsection: 'Accounting Standards'
    },
    {
      title: 'Compensation',
      description: 'Review compensation data and metrics',
      icon: 'payments',
      route: '/compensation/occupations',
      color: '#f57c00',
      section: 'Compensation',
      subsection: 'Occupations'
    },
    {
      title: 'Corporate Governance',
      description: 'Governance structures and metrics',
      icon: 'gavel',
      route: '/corporate-governance/stock-categories',
      color: '#0097a7',
      section: 'Corporate Governance',
      subsection: 'Stock Categories'
    },
    {
      title: 'ESG & Sustainability',
      description: 'Corporate social responsibility data',
      icon: 'eco',
      route: '/csr/standards',
      color: '#689f38',
      section: 'ESG',
      subsection: 'Standards'
    },
    {
      title: 'Voting Results',
      description: 'Voting results and categories',
      icon: 'how_to_vote',
      route: '/voting-results/categories',
      color: '#5e35b1',
      section: 'Voting Results',
      subsection: 'Categories'
    },
    {
      title: 'Family Firms',
      description: 'Family company status tracking',
      icon: 'family_restroom',
      route: '/family-firms/family-company-statuses',
      color: '#00897b',
      section: 'Family Firms',
      subsection: 'Family Company Statuses'
    }
  ];

  // Computed signal that filters cards based on user permissions
  quickAccessCards = computed(() => {
    return this.allCards.filter(card =>
      this.currentUserState.hasAccess(card.section, card.subsection)
    );
  });

  navigateTo(card: QuickAccessCard) {
    if (!card.disabled) {
      this.router.navigate([card.route]);
    }
  }
}
